import { Contract } from '@ekt-group/general-purpose-api-interfaces';
import { CalendarCheck02, Hash02, Settings04 } from '@untitled-ui/icons-react';
import { DatePicker } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconContainer3, IconGarbageTruck } from '@/components/icons';
import Select from '@/components/ui/Select';
import { DATE_FORMAT } from '@/constants';
import { useGetContracts } from '@/features/contracts/api/use-get-contracts';
import ServicesTable from '@/features/service-history/components/ServicesTable';
import { ServiceHistoryTab } from '@/features/service-history/types';
import { useFilterParams } from '@/hooks/use-filter-params';
import { useUserInfoStore } from '@/store';
import { cn } from '@/utils/cn';

const { RangePicker } = DatePicker;

interface ServicesTabProps {
  activeTab: ServiceHistoryTab;
  handleTabChange: (activeTab: ServiceHistoryTab) => void;
}

type DateRange = [Dayjs | null, Dayjs | null] | null;

const ServicesTab = ({ activeTab, handleTabChange }: ServicesTabProps) => {
  const { code: ownerCode } = useUserInfoStore((state) => state.selectedAccount);
  const { t } = useTranslation('serviceHistoryPage');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const { filters, setFilterValue, clearAllFilters, setFilterValues, hasFilters } = useFilterParams([
    'startDate',
    'endDate',
    'contractNumber',
  ]);

  const dateRange: DateRange = [filters.startDate ? dayjs(filters.startDate) : null, filters.endDate ? dayjs(filters.endDate) : null];

  const contractsQuery = useGetContracts<Contract[]>({
    disablePagination: true,
    ownerCodes: [ownerCode],
  });

  const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
    setFilterValues({
      startDate: dates?.[0] ? dates[0].toISOString() : null,
      endDate: dates?.[1] ? dates[1].toISOString() : null,
    });
  };

  const toggleFiltersVisibility = () => {
    setIsFiltersVisible(!isFiltersVisible);
  };

  return (
    <>
      <div className="flex flex-col gap-[10px] bg-white p-6 sm:rounded-[32px] rounded-[24px]">
        <div className="flex gap-4 flex-col sm:flex-row">
          <div className="flex w-full sm:w-auto">
            <button
              onClick={() => handleTabChange(ServiceHistoryTab.Waypoints)}
              className={cn(
                'flex flex-auto sm:flex-none items-center gap-3 justify-center rounded-[6px] text-gray-10 px-7 py-[14px]',
                activeTab === ServiceHistoryTab.Waypoints ? 'bg-brand-green-light font-medium' : 'font-normal',
              )}
            >
              <span
                className={cn('text-xl sm:text-[24px] md:text-[32px]', {
                  'font-semibold': activeTab === ServiceHistoryTab.Waypoints,
                })}
              >
                {t('waypoints')}
              </span>
              <span className="stroke-brand-brown text-transparent">
                <IconGarbageTruck size="md" />
              </span>
            </button>
            <button
              onClick={() => handleTabChange(ServiceHistoryTab.Services)}
              className={cn(
                'flex flex-auto sm:flex-none items-center gap-3 justify-center rounded-[6px] text-gray-10 px-7 py-[14px]',
                activeTab === ServiceHistoryTab.Services ? 'bg-brand-green-light font-medium' : 'font-normal',
              )}
            >
              <span
                className={cn('text-xl sm:text-[24px] md:text-[32px]', {
                  'font-semibold': activeTab === ServiceHistoryTab.Services,
                })}
              >
                {t('services')}
              </span>
              <span className="stroke-brand-brown text-transparent">
                <IconContainer3 size="md" />
              </span>
            </button>
          </div>
          <button onClick={toggleFiltersVisibility} className="flex justify-center items-center gap-1 text-[#A7AD2A]">
            <Settings04 className="w-4 h-4" />
            <span className="text-base font-medium">{t(isFiltersVisible ? 'closeFilters' : 'openFilters', { ns: 'common' })}</span>
          </button>
        </div>
        <div className={cn('flex md:flex-row flex-col flex-wrap gap-2', { hidden: !isFiltersVisible })}>
          <div className="flex gap-2 flex-1 sm:flex-row flex-col">
            <Select
              name="contractNumber"
              value={filters.contractNumber}
              onItemSelect={(value) => setFilterValue('contractNumber', value)}
              className="flex-1"
              placeholder={t('contractNumber', { ns: 'contractsTable' })}
              options={contractsQuery.data?.map((el) => ({
                value: el.contractNumber,
                label: el.contractNumber,
              }))}
              leftSide={
                <span className="w-6 h-6 text-brand-brown">
                  <Hash02 />
                </span>
              }
            />
            <RangePicker
              format={DATE_FORMAT}
              placeholder={[t('periodStart'), t('periodEnd')]}
              value={dateRange}
              onChange={handleDateRangeChange}
              suffixIcon={<CalendarCheck02 className="text-brand-brown w-6 h-6" />}
              className={cn(
                'flex-1 border bg-gray-100 md:h-12 h-10 flex justify-between items-center px-4 gap-2 cursor-pointer rounded-lg',
                dateRange && (dateRange[0] || dateRange[1]) ? 'border-gray-300 bg-white' : 'border-gray-100',
              )}
            />
            <button
              onClick={clearAllFilters}
              className={cn(
                'md:h-12 h-10 rounded-[8px] text-center col-span-1 sm:w-[85px] w-full justify-self-end items-center justify-center',
                hasFilters ? 'bg-brand-green text-gray-10' : 'sm:flex hidden bg-[#F5F5F5] text-[#B9B9B9]',
              )}
            >
              {t('clearAll')}
            </button>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-[32px]">
        <ServicesTable contractNumber={filters.contractNumber} dateRange={dateRange} />
      </div>
    </>
  );
};

export default ServicesTab;
